import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { fetchMerchantProducts, subscribeToProducts, unsubscribeFromProducts } from '../services/api';
import { useAuthStore } from '../store/useAuthStore';
import toast from 'react-hot-toast';
import { PERMISSIONS } from '../utils/permissions';
import HasPermission from '../components/HasPermission';
import { B2B_PRODUCTS } from '../utils/products';
import Swal from 'sweetalert2';


const ProductCard = ({ title, description, status, productId, onSubscribe, onUnsubscribe, isSubscribing, isUnsubscribing }) => (
  <Card className="flex flex-col h-full">
    <CardHeader>
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <CardTitle className="text-lg font-bold">{title}</CardTitle>
          <CardDescription className="mt-2 text-sm text-slate-500">{description}</CardDescription>
        </div>
        {status === 'subscribed' ? (
          <CheckCircle2 className="text-green-500 w-6 h-6 shrink-0 ml-2" />
        ) : status === 'pending' ? (
          <AlertCircle className="text-amber-500 w-6 h-6 shrink-0 ml-2" />
        ) : (
          <AlertCircle className="text-slate-400 w-6 h-6 shrink-0 ml-2" />
        )}
      </div>
    </CardHeader>
    <CardContent className="flex-1">
      <div className="text-sm font-medium text-slate-700">
        Status: <span className={
          status === 'subscribed' ? 'text-green-600' :
            status === 'pending' ? 'text-amber-600' : 'text-slate-500'
        }>
          {status === 'subscribed' ? 'Subscribed' : status === 'pending' ? 'Pending Approval' : 'Not Subscribed'}
        </span>
      </div>
    </CardContent>
    <CardFooter>
      {status === 'subscribed' ? (
        <div className="flex flex-col gap-2 w-full">
          <Link to={`/product-transactions/${productId}`} className="w-full">
            <Button variant="secondary" className="w-full">
              View Transactions
            </Button>
          </Link>
          <HasPermission permission={PERMISSIONS.MANAGE_SUBSCRIPTIONS}>
            <Button
              variant="outline"
              className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => onUnsubscribe(productId)}
              isLoading={isUnsubscribing}
            >
              Unsubscribe
            </Button>
          </HasPermission>
        </div>
      ) : (
        <HasPermission
          permission={PERMISSIONS.MANAGE_SUBSCRIPTIONS}
          fallback={<p className="text-xs text-slate-400 text-center w-full italic">Subscription management restricted</p>}
        >
          <Button
            variant="primary"
            className="w-full"
            onClick={() => onSubscribe(productId)}
            isLoading={isSubscribing}
            disabled={status === 'pending'}
          >
            {status === 'pending' ? 'Request Pending' : 'Subscribe Now'}
          </Button>
        </HasPermission>
      )}
    </CardFooter>
  </Card>
);

const SubscribedProductsPage = () => {
  const { user } = useAuthStore();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribingId, setSubscribingId] = useState(null);
  const [unsubscribingId, setUnsubscribingId] = useState(null);

  const loadData = useCallback(async () => {
    if (!user?.merchantId) return;

    setLoading(true);
    try {
      const myRequestsRes = await fetchMerchantProducts();
      const myRequests = myRequestsRes.data?.data || myRequestsRes.data || [];

      const mappedProducts = B2B_PRODUCTS.map(p => {
        const request = myRequests.find(req => req.productCode === p.productCode);
        let status = 'not_subscribed';

        if (request) {
          if (request.status === 'APPROVED' || request.subscriptionStatus === 'ACTIVE') {
            status = 'subscribed';
          } else if (request.status === 'PENDING' || request.status === 'SUBMITTED') {
            status = 'pending';
          }
        }

        return {
          ...p,
          status
        };
      });

      setProducts(mappedProducts);
    } catch (error) {
      toast.error('Failed to load products and services');
    } finally {
      setLoading(false);
    }
  }, [user?.merchantId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubscribe = async (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;

    setSubscribingId(productId);
    try {
      await subscribeToProducts([product.productCode]);
      toast.success(`Subscription request sent for ${product.title}`);
      loadData();
    } catch (error) {
      toast.error(`Failed to subscribe to ${product.title}`);
    } finally {
      setSubscribingId(null);
    }
  };

  const handleUnsubscribe = async (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to unsubscribe from ${product.title}. You will no longer be able to access its features.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#0F172A',
      cancelButtonColor: '#e2e8f0',
      confirmButtonText: 'Yes, unsubscribe',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      customClass: {
        confirmButton: 'px-4 py-2 rounded-lg font-semibold',
        cancelButton: 'px-4 py-2 rounded-lg font-semibold text-slate-600'
      }
    });

    if (!result.isConfirmed) return;

    setUnsubscribingId(productId);
    try {
      await unsubscribeFromProducts(product.productCode);
      toast.success(`Unsubscribed from ${product.title}`);
      loadData();
    } catch (error) {
      toast.error(`Failed to unsubscribe from ${product.title}`);
    } finally {
      setUnsubscribingId(null);
    }
  };

  if (loading && products.length === 0) {
    return (
      <div className="h-96 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <h2 className="text-2xl font-bold tracking-tight text-slate-900">Products & Services</h2>
        <p className="text-muted-foreground">
          Manage your active product subscriptions and explore new financial solutions.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {products.map((product) => (
          <ProductCard
            key={product.id}
            productId={product.id}
            title={product.title}
            description={product.description}
            status={product.status}
            onSubscribe={handleSubscribe}
            onUnsubscribe={handleUnsubscribe}
            isSubscribing={subscribingId === product.id}
            isUnsubscribing={unsubscribingId === product.id}
          />
        ))}
      </div>
    </div>
  );
};

export default SubscribedProductsPage;

